import React, { useState } from 'react';

/**
 * RatingManager Component
 * 
 * Enterprise-style rating component for candidate evaluation
 * Supports category-based ratings: Technical, Experience, Culture Fit, Communication
 */
const RatingManager = ({ ratings = {}, onRatingChange, readOnly = false }) => {
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [hoveredStar, setHoveredStar] = useState(null);

  const categories = [
    { key: 'technical', label: 'Technical Skills' },
    { key: 'experience', label: 'Experience Level' },
    { key: 'cultureFit', label: 'Culture Fit' },
    { key: 'communication', label: 'Communication' }
  ];

  const handleStarClick = (category, value) => {
    if (readOnly) return;
    
    const newRatings = {
      ...ratings,
      [category]: value
    };

    // Calculate overall average
    const categoryValues = categories
      .map(cat => newRatings[cat.key])
      .filter(val => val !== undefined && val !== null);
    
    if (categoryValues.length > 0) {
      const avg = categoryValues.reduce((sum, val) => sum + val, 0) / categoryValues.length;
      newRatings.overall = Math.round(avg * 2) / 2; // Round to nearest 0.5
    }

    if (onRatingChange) {
      onRatingChange(newRatings, newRatings.overall);
    }
  };

  const renderStars = (category, value) => {
    const stars = [];
    const currentValue = value || 0;
    const hoverValue = hoveredCategory === category && hoveredStar ? hoveredStar : null;

    for (let i = 1; i <= 5; i++) {
      const isActive = hoverValue ? i <= hoverValue : i <= currentValue;
      stars.push(
        <span
          key={i}
          style={{
            fontSize: '1.25rem',
            color: isActive ? '#F59E0B' : '#E5E7EB',
            cursor: readOnly ? 'default' : 'pointer',
            marginRight: '0.125rem'
          }}
          onMouseEnter={() => {
            if (!readOnly) {
              setHoveredCategory(category);
              setHoveredStar(i);
            }
          }}
          onMouseLeave={() => {
            if (!readOnly) {
              setHoveredCategory(null);
              setHoveredStar(null);
            }
          }}
          onClick={() => handleStarClick(category, i)}
          title={readOnly ? null : `Rate ${i} out of 5`}
        >
          ★
        </span>
      );
    }
    return stars;
  };

  return (
    <div style={{ padding: '1rem 0' }}>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(2, 1fr)', 
        gap: '1rem',
        marginBottom: ratings.overall ? '1rem' : '0'
      }}>
        {categories.map(category => (
          <div key={category.key} style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ 
              fontSize: '0.875rem', 
              fontWeight: '500', 
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              {category.label}
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {renderStars(category.key, ratings[category.key])}
              {ratings[category.key] && (
                <span style={{ 
                  fontSize: '0.875rem', 
                  color: '#6B7280',
                  fontWeight: '500'
                }}>
                  {ratings[category.key]}/5
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {ratings.overall && (
        <div style={{
          padding: '0.75rem',
          background: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: '6px',
          marginTop: '1rem'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Overall Rating:
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1rem', color: '#F59E0B' }}>
                {'★'.repeat(Math.floor(ratings.overall))}
                {ratings.overall % 1 >= 0.5 && '☆'}
                {'☆'.repeat(5 - Math.ceil(ratings.overall))}
              </span>
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                {ratings.overall}/5
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RatingManager;
